import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Repeat, Users, Pencil, CalendarOff, CalendarCheck, Loader2, Check, Clock, UserPlus, CalendarRange } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AvatarStack } from '../ui/avatar-stack';
import type { ScheduleOccurrence } from '../../types/schedule-event';

interface ScheduleEventCardProps {
  occurrence: ScheduleOccurrence;
  canManage: boolean;
  isAdmin: boolean;
  /** True while a register/unregister call for this occurrence is in flight. */
  busy: boolean;
  isLoggedIn: boolean;
  onRegister: (occurrence: ScheduleOccurrence) => void;
  onUnregister: (occurrence: ScheduleOccurrence) => void;
  onShowAttendees: (occurrence: ScheduleOccurrence) => void;
  onEdit: (occurrence: ScheduleOccurrence) => void;
  onToggleCancelled: (occurrence: ScheduleOccurrence) => void;
  onAddUser: (occurrence: ScheduleOccurrence) => void;
  onBulkRegister: (occurrence: ScheduleOccurrence) => void;
}

export function ScheduleEventCard({
  occurrence,
  canManage,
  isAdmin,
  busy,
  isLoggedIn,
  onRegister,
  onUnregister,
  onShowAttendees,
  onEdit,
  onToggleCancelled,
  onAddUser,
  onBulkRegister,
}: ScheduleEventCardProps) {
  const { t } = useTranslation();

  const timeRange = `${format(parseISO(occurrence.starts_at), 'HH:mm')} – ${format(parseISO(occurrence.ends_at), 'HH:mm')}`;
  const isPast = parseISO(occurrence.ends_at) < new Date();
  const canSelfRegister = !occurrence.staff_only_registration;
  const isPending = occurrence.registration_status === 'pending';

  // AvatarStack shares SenderPreview's shape but wants a non-optional avatar url.
  const previewUsers = occurrence.attendees_preview.map(a => ({
    id: a.id,
    username: a.username,
    slug: a.slug,
    avatar_small_url: a.avatar_small_url ?? null,
  }));

  const registrationControl = () => {
    if (occurrence.registered) {
      const label = isPending ? t('schedule.pending') : t('schedule.registered');
      // Users can only remove themselves from events that allow self-registration.
      if (!canSelfRegister) {
        return (
          <Badge variant={isPending ? 'secondary' : 'outline'} className="gap-1">
            {isPending ? <Clock className="h-3 w-3" /> : <Check className="h-3 w-3" />}
            {label}
          </Badge>
        );
      }
      return (
        <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={() => onUnregister(occurrence)}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isPending ? <Clock className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          {label}
        </Button>
      );
    }

    if (isPast || occurrence.cancelled) return null;

    if (!canSelfRegister) {
      return <span className="text-xs text-muted-foreground text-right">{t('schedule.staffManagesRegistration')}</span>;
    }
    if (occurrence.full) {
      return (
        <Button variant="outline" size="sm" disabled>
          {t('schedule.full')}
        </Button>
      );
    }
    return (
      <Button size="sm" disabled={busy} onClick={() => onRegister(occurrence)}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isLoggedIn ? t('schedule.register') : t('schedule.loginToRegister')}
      </Button>
    );
  };

  return (
    <div
      className={`relative overflow-hidden rounded-lg border ${occurrence.cancelled ? 'opacity-60' : ''}`}
      data-testid="schedule-event-card"
    >
      {/* Event color: solid strip on the left, faint tint behind the content */}
      <div className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: occurrence.color }} />
      <div className="p-3 pl-5" style={{ backgroundColor: `${occurrence.color}14` }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{timeRange}</span>
              {occurrence.recurring && <Repeat className="h-3.5 w-3.5" aria-label={t('schedule.recurring')} />}
              {occurrence.cancelled && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                  {t('schedule.cancelled')}
                </Badge>
              )}
              {canManage && occurrence.pending_count > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 gap-1">
                  <Clock className="h-2.5 w-2.5" />
                  {t('schedule.pendingCount', { count: occurrence.pending_count })}
                </Badge>
              )}
            </div>
            <h3 className={`text-sm font-semibold mt-0.5 truncate ${occurrence.cancelled ? 'line-through' : ''}`}>
              {occurrence.title}
            </h3>
            {occurrence.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{occurrence.description}</p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => onShowAttendees(occurrence)}
              aria-label={t('schedule.attendees.title', { count: occurrence.registrations_count })}
            >
              {previewUsers.length > 0 ? (
                <AvatarStack users={previewUsers} totalCount={occurrence.registrations_count} max={3} size="sm" />
              ) : (
                <Users className="h-3.5 w-3.5" />
              )}
              <span>
                {occurrence.registrations_count}
                {occurrence.max_participants != null && `/${occurrence.max_participants}`}
              </span>
            </button>
            {registrationControl()}
          </div>
        </div>

        {canManage && (
          <div className="flex items-center justify-end flex-wrap gap-1 mt-2 pt-2 border-t border-border/50">
            {!isPast && !occurrence.cancelled && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => onAddUser(occurrence)}
              >
                <UserPlus className="h-3.5 w-3.5" />
                {t('schedule.addUser.action')}
              </Button>
            )}
            {isAdmin && occurrence.recurring && !isPast && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => onBulkRegister(occurrence)}
              >
                <CalendarRange className="h-3.5 w-3.5" />
                {t('schedule.bulkRegister.action')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => onEdit(occurrence)}
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('common.edit')}
            </Button>
            {!isPast && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => onToggleCancelled(occurrence)}
              >
                {occurrence.cancelled ? (
                  <>
                    <CalendarCheck className="h-3.5 w-3.5" />
                    {t('schedule.restoreOccurrence')}
                  </>
                ) : (
                  <>
                    <CalendarOff className="h-3.5 w-3.5" />
                    {t('schedule.cancelOccurrence')}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
